import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { Cargando, display, panel } from '../asistencia/ui';
import { Encabezado, Vacio } from './ui';
import { armarElementos } from './comun';

const NO_ENTRA = 0;      // "Restringido 24-horas" en GeoVision
const SIEMPRE = 255;     // "Acceso 24-horas"

const nombreHorario = (h) => (h?.id === NO_ENTRA ? 'No puede' : h?.id === SIEMPRE ? 'Las 24 horas' : h?.nombre || 'Horario');

const TONO_CELDA = {
    no: 'bg-white text-slate-400 border-slate-200 border-dashed',
    siempre: 'bg-sura-blue text-white border-sura-blue',
    horario: 'bg-cyan-50 text-cyan-800 border-cyan-200',
};
const tonoDe = (h) => (h?.id === NO_ENTRA ? TONO_CELDA.no : h?.id === SIEMPRE ? TONO_CELDA.siempre : TONO_CELDA.horario);

// Una celda: si el grupo puede usar esa puerta y con qué horario
const Celda = ({ horario, horarios, editable, ocupada, onCambio, etiqueta }) => {
    if (!editable) {
        return <span className={`flex items-center justify-center h-10 px-2 rounded-lg border text-xs text-center ${tonoDe(horario)}`}>{nombreHorario(horario)}</span>;
    }
    return (
        <label className="block">
            <span className="sr-only">{etiqueta}</span>
            <select
                value={horario?.id ?? ''} disabled={ocupada} onChange={(e) => onCambio(Number(e.target.value))}
                className={`w-full h-10 px-2 rounded-lg border text-xs text-center cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 disabled:opacity-50 ${tonoDe(horario)}`}
            >
                {horarios.map((h) => <option key={h.id} value={h.id}>{nombreHorario(h)}</option>)}
            </select>
        </label>
    );
};

/**
 * Grupos de acceso: cada fila es un grupo, cada columna una puerta, y en el cruce si
 * puede usarla y con qué horario. Cada tarjeta pertenece a un grupo (se cambia en Personas).
 */
const GruposVista = ({ vivo }) => {
    const [datos, setDatos] = useState(null);
    const [error, setError] = useState(null);
    const [ocupada, setOcupada] = useState(null);

    const cargar = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/puertas/grupos');
            setDatos(data);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudieron cargar los grupos');
        }
    }, []);
    useEffect(() => { cargar(); }, [cargar]);

    // Columnas: la santamaría (abrir y cerrar) y cada ascensor
    const columnas = useMemo(() => {
        const elementos = armarElementos(vivo.estado?.puertas || [], null);
        return elementos.map((el) => (el.tipo === 'santamaria'
            ? { nombre: el.nombre, puertas: [el.abre && { id: el.abre.id, nombre: 'Abrir' }, el.cierra && { id: el.cierra.id, nombre: 'Cerrar' }].filter(Boolean) }
            : { nombre: el.nombre, puertas: [{ id: el.puerta.id, nombre: null }] }));
    }, [vivo.estado]);
    const puertas = columnas.flatMap((c) => c.puertas);

    const horarioDe = (grupo, puertaId) => {
        const p = grupo.puertas.find((x) => x.puerta_id === puertaId);
        return p ? (datos.horarios.find((h) => h.id === p.horario_id) || { id: p.horario_id, nombre: p.horario }) : null;
    };

    const cambiar = async (grupo, puertaId, horarioId) => {
        const p = grupo.puertas.find((x) => x.puerta_id === puertaId);
        if (!p) return;
        setOcupada(`${grupo.id}-${puertaId}`);
        try {
            await axiosInstance.put(`/api/puertas/grupos/${grupo.id}`, {
                puertas: [{ controlador_id: p.controlador_id, puerta_id: puertaId, horario_id: horarioId }],
            });
            toast.success(`${grupo.nombre}: ${nombreHorario(datos.horarios.find((h) => h.id === horarioId)).toLowerCase()} en ${p.puerta}`);
            await cargar();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cambiar el horario');
        } finally {
            setOcupada(null);
        }
    };

    return (
        <div className="space-y-6">
            <Encabezado titulo="Grupos de acceso">
                Qué puede usar cada grupo y en qué horario. Los horarios se crean en GeoVision; aquí se eligen.
            </Encabezado>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <div className={`${panel} overflow-hidden`}>
                {(!datos && !error) || !vivo.estado ? <Cargando /> : !datos?.grupos?.length ? (
                    <Vacio icono={ShieldCheck} titulo="No hay grupos de acceso" />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-separate border-spacing-x-1.5 border-spacing-y-1.5 px-3 py-3">
                            <thead>
                                <tr>
                                    <th rowSpan={2} className="text-left align-bottom pl-2 pb-1 text-xs font-medium text-slate-500">Grupo</th>
                                    {columnas.map((c) => (
                                        <th
                                            key={c.nombre} colSpan={c.puertas.length} rowSpan={c.puertas[0]?.nombre ? 1 : 2}
                                            className={`${display} text-[13px] font-bold text-slate-900 text-center px-1 pb-1 ${c.puertas[0]?.nombre ? 'border-b border-slate-100' : 'align-bottom'} min-w-[7.5rem]`}
                                        >
                                            {c.nombre}
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {columnas.filter((c) => c.puertas[0]?.nombre).flatMap((c) => c.puertas).map((p) => (
                                        <th key={p.id} className="text-xs font-medium text-slate-500 text-center px-1 pb-1 min-w-[7.5rem]">{p.nombre}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {datos.grupos.map((g) => (
                                    <tr key={g.id}>
                                        <th scope="row" className="text-left pl-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{g.nombre}</th>
                                        {puertas.map((p) => (
                                            <td key={p.id}>
                                                <Celda
                                                    horario={horarioDe(g, p.id)} horarios={datos.horarios}
                                                    editable={datos.puede_gestionar && g.editable && g.puertas.some((x) => x.puerta_id === p.id)}
                                                    ocupada={ocupada === `${g.id}-${p.id}`}
                                                    onCambio={(h) => cambiar(g, p.id, h)}
                                                    etiqueta={`${g.nombre}: ${columnas.find((c) => c.puertas.includes(p))?.nombre} ${p.nombre || ''}`}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <p className="text-xs text-slate-500 flex flex-wrap gap-x-5 gap-y-1">
                <span><span className="inline-block w-3 h-3 rounded-[4px] align-[-2px] mr-1.5 bg-sura-blue" />Las 24 horas</span>
                <span><span className="inline-block w-3 h-3 rounded-[4px] align-[-2px] mr-1.5 bg-cyan-50 border border-cyan-200" />Con horario</span>
                <span><span className="inline-block w-3 h-3 rounded-[4px] align-[-2px] mr-1.5 bg-white border border-dashed border-slate-300" />No puede</span>
            </p>
        </div>
    );
};

export default GruposVista;
